import { initializeApp } from 'firebase/app';
import { getFirestore, collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy, setDoc } from 'firebase/firestore';

// ─── CONFIG ────────────────────────────────────────────────────────────────
const USE_FIREBASE = true; // set true to switch to real Firestore

const firebaseConfig = {
  apiKey: "AIzaSyCyTYlLz8j7Gph96tB-X_WU-dLbtinEBcM",
  authDomain: "digvijay-industries.firebaseapp.com",
  projectId: "digvijay-industries",
  storageBucket: "digvijay-industries.firebasestorage.app",
  messagingSenderId: "1040415616420",
  appId: "1:1040415616420:web:7697b4ca2ed84c78e2e531",
  measurementId: "G-7W3EE0QZKQ"
};

let db = null;
try {
  if (USE_FIREBASE) {
    const app = initializeApp(firebaseConfig);
    db = getFirestore(app);
  }
} catch (error) {
  console.error("Firebase initialization error:", error);
  db = null;
}

// ─── LOCAL STORAGE HELPERS ─────────────────────────────────────────────────
const lsGet = (key, fallback = []) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
};
const lsSet = (key, data) => localStorage.setItem(key, JSON.stringify(data));
const uid = () => '_' + Math.random().toString(36).substr(2, 9) + Date.now();

// ─── SEED DATA REMOVED ───────────────────────────────────────────────────────
// All seed data removed. Database starts empty.
// Add data through admin panel.

// To clear existing localStorage data, run this in browser console:
// localStorage.clear();

// ─── PRODUCTS ──────────────────────────────────────────────────────────────
export const getProducts = async () => {
  if (USE_FIREBASE) {
    const snap = await getDocs(collection(db, 'products'));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }
  return lsGet('dgi_products_v3', []);
};

export const getFeaturedProducts = async () => {
  if (USE_FIREBASE) {
    try {
      const snap = await getDocs(query(collection(db, 'products'), where('featured', '==', true)));
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.warn('Firebase fetch failed, falling back to local storage', e);
    }
  }
  return lsGet('dgi_products_v3', []).filter(p => p.featured);
};

export const getProductById = async (id) => {
  const all = await getProducts();
  return all.find(p => p.id === id) || null;
};

export const addProduct = async (data) => {
  if (USE_FIREBASE) {
    const ref = await addDoc(collection(db, 'products'), data);
    return ref.id;
  }
  const items = lsGet('dgi_products_v3', []);
  const newItem = { ...data, id: uid(), createdAt: new Date().toISOString() };
  lsSet('dgi_products_v3', [...items, newItem]);
  return newItem.id;
};

export const updateProduct = async (id, data) => {
  if (USE_FIREBASE) { await updateDoc(doc(db, 'products', id), data); return; }
  const items = lsGet('dgi_products_v3', []);
  lsSet('dgi_products_v3', items.map(i => i.id === id ? { ...i, ...data } : i));
};

export const deleteProduct = async (id) => {
  if (USE_FIREBASE) { await deleteDoc(doc(db, 'products', id)); return; }
  lsSet('dgi_products_v3', lsGet('dgi_products_v3', []).filter(i => i.id !== id));
};

// ─── SERVICES ──────────────────────────────────────────────────────────────
export const getServices = async () => {
  if (USE_FIREBASE) {
    try {
      const snap = await getDocs(collection(db, 'services'));
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.warn('Firebase fetch failed, falling back to local storage', e);
    }
  }
  return lsGet('dgi_services_v3', []);
};

export const addService = async (data) => {
  if (USE_FIREBASE) {
    const ref = await addDoc(collection(db, 'services'), data);
    return ref.id;
  }
  const items = lsGet('dgi_services_v3', []);
  const newItem = { ...data, id: uid(), createdAt: new Date().toISOString() };
  lsSet('dgi_services_v3', [...items, newItem]);
  return newItem.id;
};

export const updateService = async (id, data) => {
  if (USE_FIREBASE) { await updateDoc(doc(db, 'services', id), data); return; }
  const items = lsGet('dgi_services_v3', []);
  lsSet('dgi_services_v3', items.map(i => i.id === id ? { ...i, ...data } : i));
};

export const deleteService = async (id) => {
  if (USE_FIREBASE) { await deleteDoc(doc(db, 'services', id)); return; }
  lsSet('dgi_services_v3', lsGet('dgi_services_v3', []).filter(i => i.id !== id));
};

// ─── BLOGS ─────────────────────────────────────────────────────────────────
export const getBlogs = async () => {
  if (USE_FIREBASE) {
    const snap = await getDocs(query(collection(db, 'blogs'), orderBy('createdAt', 'desc')));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }
  const blogs = lsGet('dgi_blogs_v3', []);
  return [...blogs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

export const getBlogById = async (id) => {
  const blogs = await getBlogs();
  return blogs.find(b => b.id === id) || null;
};

export const addBlog = async (data) => {
  if (USE_FIREBASE) {
    const ref = await addDoc(collection(db, 'blogs'), { ...data, createdAt: new Date().toISOString() });
    return ref.id;
  }
  const items = lsGet('dgi_blogs_v3', []);
  const newItem = { ...data, id: uid(), createdAt: new Date().toISOString() };
  lsSet('dgi_blogs_v3', [...items, newItem]);
  return newItem.id;
};

export const updateBlog = async (id, data) => {
  if (USE_FIREBASE) { await updateDoc(doc(db, 'blogs', id), data); return; }
  const items = lsGet('dgi_blogs_v3', []);
  lsSet('dgi_blogs_v3', items.map(i => i.id === id ? { ...i, ...data } : i));
};

export const deleteBlog = async (id) => {
  if (USE_FIREBASE) { await deleteDoc(doc(db, 'blogs', id)); return; }
  lsSet('dgi_blogs_v3', lsGet('dgi_blogs_v3', []).filter(i => i.id !== id));
};

// ─── ENQUIRIES ─────────────────────────────────────────────────────────────
export const saveEnquiry = async (data) => {
  try {
    if (USE_FIREBASE) {
      await addDoc(collection(db, 'enquiries'), { ...data, createdAt: new Date().toISOString() });
      return true;
    }
    const items = lsGet('dgi_enquiries_v3', []);
    const newItem = { ...data, id: uid(), createdAt: new Date().toISOString(), status: 'New' };
    lsSet('dgi_enquiries_v3', [...items, newItem]);
    return true;
  } catch { return false; }
};

export const getEnquiries = async () => {
  if (USE_FIREBASE) {
    const snap = await getDocs(collection(db, 'enquiries'));
    return snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }
  return lsGet('dgi_enquiries_v3', []);
};

export const updateEnquiryStatus = async (id, status) => {
  if (USE_FIREBASE) {
    try { await updateDoc(doc(db, 'enquiries', id), { status }); } catch {}
    return;
  }
  const items = lsGet('dgi_enquiries_v3', []);
  lsSet('dgi_enquiries_v3', items.map(i => i.id === id ? { ...i, status } : i));
};

export const deleteEnquiry = async (id) => {
  if (USE_FIREBASE) {
    await deleteDoc(doc(db, 'enquiries', id));
    return;
  }
  lsSet('dgi_enquiries_v3', lsGet('dgi_enquiries_v3', []).filter(i => i.id !== id));
};

// ─── CAREER APPLICATIONS ───────────────────────────────────────────────────
export const saveCareerApplication = async (data) => {
  try {
    if (USE_FIREBASE) {
      await addDoc(collection(db, 'careers'), { ...data, createdAt: new Date().toISOString() });
      return true;
    }
    const items = lsGet('dgi_careers_v3', []);
    const newItem = { ...data, id: uid(), createdAt: new Date().toISOString(), status: 'New' };
    lsSet('dgi_careers_v3', [...items, newItem]);
    return true;
  } catch { return false; }
};

export const getCareerApplications = async () => {
  if (USE_FIREBASE) {
    const snap = await getDocs(collection(db, 'careers'));
    return snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }
  return lsGet('dgi_careers_v3', []);
};

export const updateCareerStatus = async (id, status) => {
  if (USE_FIREBASE) {
    try { await updateDoc(doc(db, 'careers', id), { status }); } catch {}
    return;
  }
  const items = lsGet('dgi_careers_v3', []);
  lsSet('dgi_careers_v3', items.map(i => i.id === id ? { ...i, status } : i));
};

export const deleteCareerApplication = async (id) => {
  if (USE_FIREBASE) {
    await deleteDoc(doc(db, 'careers', id));
    return;
  }
  lsSet('dgi_careers_v3', lsGet('dgi_careers_v3', []).filter(i => i.id !== id));
};


// ─── TESTIMONIALS ──────────────────────────────────────────────────────────
export const getTestimonials = async () => {
  if (USE_FIREBASE) {
    try {
      const snap = await getDocs(collection(db, 'testimonials'));
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.warn('Firebase fetch failed, falling back to local storage', e);
    }
  }
  return lsGet('dgi_testimonials_v3', []);
};

export const addTestimonial = async (data) => {
  if (USE_FIREBASE) {
    const ref = await addDoc(collection(db, 'testimonials'), data);
    return ref.id;
  }
  const items = lsGet('dgi_testimonials_v3', []);
  const newItem = { ...data, id: uid(), createdAt: new Date().toISOString() };
  lsSet('dgi_testimonials_v3', [...items, newItem]);
  return newItem.id;
};

export const updateTestimonial = async (id, data) => {
  if (USE_FIREBASE) { await updateDoc(doc(db, 'testimonials', id), data); return; }
  const items = lsGet('dgi_testimonials_v3', []);
  lsSet('dgi_testimonials_v3', items.map(i => i.id === id ? { ...i, ...data } : i));
};

export const deleteTestimonial = async (id) => {
  if (USE_FIREBASE) { await deleteDoc(doc(db, 'testimonials', id)); return; }
  lsSet('dgi_testimonials_v3', lsGet('dgi_testimonials_v3', []).filter(i => i.id !== id));
};

// ─── FAQs ────────────────────────────────────────────────────────────────────
export const getFAQs = async () => {
  if (USE_FIREBASE) {
    const snap = await getDocs(collection(db, 'faqs'));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }
  return lsGet('dgi_faqs_v3', []);
};

export const addFAQ = async (data) => {
  if (USE_FIREBASE) {
    const ref = await addDoc(collection(db, 'faqs'), data);
    return ref.id;
  }
  const items = lsGet('dgi_faqs_v3', []);
  const newItem = { ...data, id: uid(), createdAt: new Date().toISOString() };
  lsSet('dgi_faqs_v3', [...items, newItem]);
  return newItem.id;
};

export const updateFAQ = async (id, data) => {
  if (USE_FIREBASE) { await updateDoc(doc(db, 'faqs', id), data); return; }
  const items = lsGet('dgi_faqs_v3', []);
  lsSet('dgi_faqs_v3', items.map(i => i.id === id ? { ...i, ...data } : i));
};

export const deleteFAQ = async (id) => {
  if (USE_FIREBASE) { await deleteDoc(doc(db, 'faqs', id)); return; }
  lsSet('dgi_faqs_v3', lsGet('dgi_faqs_v3', []).filter(i => i.id !== id));
};

// Firestore query helpers (unused but kept for Firebase mode)
export { query, where };

// ─── COMPANY INFO ────────────────────────────────────────────────────────────
const DEFAULT_COMPANY_INFO = {
  name: "Digvijay Industries",
  email: "contact@digvijayindia.com",
  phone: "8956403388",
  address: "J-79, MIDC, Kupwad, Sangli Maharashtra - 416436",
  openingHours: "09.00 am to 06.00 pm | Weekly Off: Tuesday"
};

const sanitizeCompanyInfo = (info) => {
  if (!info) return info;
  const rawPhone = info.phone ? String(info.phone) : '8956403388';
  const cleanPhone = rawPhone
    .split(/[,/|]/)
    .map(p => p.trim())
    .filter(p => !p.includes('2644484') && !p.includes('0233') && p.length > 0)
    .join(', ') || '8956403388';
  return {
    ...info,
    phone: cleanPhone
  };
};

export const getCompanyInfo = async () => {
  // Clear any old cached number from localStorage
  try {
    const raw = localStorage.getItem('dgi_company_info');
    if (raw && (raw.includes('2644484') || raw.includes('0233'))) {
      const parsed = JSON.parse(raw);
      if (parsed) {
        localStorage.setItem('dgi_company_info', JSON.stringify(sanitizeCompanyInfo(parsed)));
      }
    }
  } catch (e) {}

  if (USE_FIREBASE) {
    try {
      const snap = await getDocs(collection(db, 'settings'));
      const infoDoc = snap.docs.find(d => d.id === 'company_info');
      if (infoDoc) return sanitizeCompanyInfo(infoDoc.data());
      // If it doesn't exist, create it with defaults
      await updateDoc(doc(db, 'settings', 'company_info'), DEFAULT_COMPANY_INFO).catch(async () => {
        // If it fails because doc doesn't exist, use setDoc instead via addDoc/doc fallback
        await setDoc(doc(db, 'settings', 'company_info'), DEFAULT_COMPANY_INFO);
      });
      return sanitizeCompanyInfo(DEFAULT_COMPANY_INFO);
    } catch (e) {
      return sanitizeCompanyInfo(DEFAULT_COMPANY_INFO);
    }
  }
  return sanitizeCompanyInfo(lsGet('dgi_company_info', DEFAULT_COMPANY_INFO));
};

export const updateCompanyInfo = async (data) => {
  if (USE_FIREBASE) {
    await setDoc(doc(db, 'settings', 'company_info'), data, { merge: true });
    return;
  }
  lsSet('dgi_company_info', data);
};

